import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { accountDeletionMessageKey, confirmaConContrasena, requestAccountDeletion } from "@/lib/accountDeletion";

/**
 * La confirmación de «Eliminar mi cuenta», la misma para todos los roles (D7).
 * Antes había dos copias (Ajustes y Perfil del perito) con el mismo texto y
 * respuestas distintas; ahora la llamada y los mensajes salen de `accountDeletion.ts`.
 * Quien entra con Google o Apple no tiene contraseña: confirma escribiendo su nombre.
 */
export default function DeleteAccountDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const { user, profile } = useAuth();
  const conContrasena = confirmaConContrasena(user);
  const nombreDeLaCuenta = profile?.full_name?.trim() || user?.email || "";
  // Un solo campo: la contraseña o, sin ella, el nombre de la cuenta.
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const close = () => {
    onOpenChange(false);
    setPassword("");
  };

  const submit = async () => {
    if (!password.trim()) {
      toast.error(t(conContrasena ? "settings.account.passwordRequired" : "settings.account.nameRequired"));
      return;
    }
    setLoading(true);
    try {
      const result = await requestAccountDeletion(
        conContrasena ? { password } : { nameConfirmation: password },
        i18n.language,
      );
      const key = accountDeletionMessageKey(result);
      if (result.ok) {
        toast.success(t(key), { duration: 8000 });
        close();
      } else {
        toast.error(t(key));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="w-5 h-5" />
            {t("settings.account.confirmTitle")}
          </DialogTitle>
          <DialogDescription className="space-y-3 pt-2">
            <span className="block">{t("settings.account.confirmDescription")}</span>
            <span className="block bg-muted p-3 rounded-lg text-sm space-y-1">
              <span className="block">• {t("settings.account.deleteItem1")}</span>
              <span className="block">• {t("settings.account.deleteItem2")}</span>
              <span className="block">• {t("settings.account.deleteItem3")}</span>
            </span>
            <span className="block font-medium">{t("settings.account.processTime")}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          {conContrasena ? (
            <>
              <Label htmlFor="delete-password">{t("settings.account.enterPassword")}</Label>
              <Input
                id="delete-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("settings.account.passwordPlaceholder")}
                onKeyDown={(e) => e.key === "Enter" && submit()}
              />
            </>
          ) : (
            <>
              <Label htmlFor="delete-name">{t("settings.account.enterName")}</Label>
              <Input
                id="delete-name"
                type="text"
                autoComplete="off"
                autoCapitalize="words"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={nombreDeLaCuenta}
                onKeyDown={(e) => e.key === "Enter" && submit()}
              />
              <p className="text-xs text-muted-foreground">{t("settings.account.enterNameHint")}</p>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>
            {t("common.cancel")}
          </Button>
          <Button variant="destructive" onClick={submit} disabled={loading || !password.trim()}>
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {t("settings.account.confirmDelete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
